import { useQuery } from "@apollo/client";
import {
  ArrowBack as ArrowBackIcon,
  Mail,
  MailOutline,
  PieChart,
  PieChartOutline,
} from "@mui/icons-material";
import { ReactElement } from "react";
import { RouteObject, useParams } from "react-router-dom";

import {
  IntegrationServiceType,
  ListIntegrationsQuery,
  ListIntegrationsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import Layout from "~frontend/components/Layout";
import Redirect from "~frontend/components/Redirect";
import { PrimarySidebarItem } from "~frontend/components/Sidebar";
import Campaign from "~frontend/components/icons/Campaign";
import CampaignFilled from "~frontend/components/icons/CampaignFilled";
import DonationHistory from "~frontend/components/icons/DonationHistory";
import DonationHistoryFilled from "~frontend/components/icons/DonationHistoryFilled";
import Integration from "~frontend/components/icons/Integration";
import IntegrationFilled from "~frontend/components/icons/IntegrationFilled";
import OptOut from "~frontend/components/icons/OptOut";
import OptOutFilled from "~frontend/components/icons/OptOutFilled";
import Person from "~frontend/components/icons/Person";
import PersonFilled from "~frontend/components/icons/PersonFilled";
import PersonalizedGifsIcon from "~frontend/components/icons/PersonalizedGifs";
import PersonalizedGifsIconFilled from "~frontend/components/icons/PersonalizedGifsFilled";
import Phone from "~frontend/components/icons/Phone";
import PhoneFilled from "~frontend/components/icons/PhoneFilled";
import Script from "~frontend/components/icons/Script";
import ScriptFilled from "~frontend/components/icons/ScriptFilled";
import Settings from "~frontend/components/icons/Settings";
import SettingsFilled from "~frontend/components/icons/SettingsFilled";
import SubscribersIcon from "~frontend/components/icons/Subscribers";
import SubscribersIconFilled from "~frontend/components/icons/SubscribersFilled";
import Tag from "~frontend/components/icons/Tag";
import TagFilled from "~frontend/components/icons/TagFilled";
import Thread from "~frontend/components/icons/Thread";
import ThreadFilled from "~frontend/components/icons/ThreadFilled";
import Usage from "~frontend/components/icons/Usage";
import UsageFilled from "~frontend/components/icons/UsageFilled";
import { LIST_INTEGRATIONS } from "~frontend/graphql/integration";
import CacheClearingApolloProvider from "~frontend/providers/CacheClearingApolloProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import SubaccountProvider, {
  subaccountLandingPage,
  useSubaccount,
} from "~frontend/providers/SubaccountProvider";
import { InboxAllCampaigns } from "~frontend/routes/subaccounts/inboxes/InboxAllCampaigns";
import {
  userIsSubaccountAdminOrHigher,
  userIsSubaccountSupervolunteerOrHigher,
} from "~frontend/util/roles";

import { Approver } from "./Approver";
import SubaccountAnalytics from "./analytics";
import { AudiencesPage } from "./audiences";
import { AudienceSettingsPage } from "./audiences/AudienceSettings";
import { CampaignEditor } from "./campaignEditor";
import { CampaignView } from "./campaignView";
import { CampaignErrors } from "./campaignView/CampaignErrors";
import { CampaignsList } from "./campaignsList";
import { SubaccountDonationHistory } from "./donationHistory";
import { InboxManagement } from "./inboxManagement";
import { InboxList } from "./inboxes";
import { Inbox } from "./inboxes/Inbox";
import { InboxArchived } from "./inboxes/InboxArchived";
import { InboxCampaign } from "./inboxes/InboxCampaign";
import { IntegrationList } from "./integrationList";
import { OptOuts } from "./optOuts";
import { PersonalizedGifs } from "./personalizedGifs";
import { PhoneNumbersList } from "./phoneNumberList";
import { Registration } from "./phoneNumberList/TenDlc/Registration";
import TollFreeRegistration from "./phoneNumberList/TollFreeRegistration";
import PhoneNumberPoolDetails from "./phoneNumberList/pools/PhoneNumberPoolDetails";
import { SubaccountResponseScripts } from "./scripts";
import { SubaccountSettings } from "./settings";
import Tags from "./tags";
import { ThreadsList } from "./threads";
import SubaccountUsage from "./usage";
import SubaccountUsers from "./users";

function SubaccountRootRedirect(): ReactElement {
  const { landingPage } = useSubaccount()!;

  return <Redirect to={landingPage} />;
}

function ShortcodeRedirect(): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  return <Redirect to={subaccountLink("/audiences")} />;
}

function SubaccountLayoutInner(): ReactElement {
  const intl = useIntl();
  const { subaccountLink, subaccount, membership, donationHistoryPageEnabled } =
    useSubaccount()!;
  const personalizedGifsEnabled = useFeatureFlag("personalizedGifs");

  const isAdmin = userIsSubaccountAdminOrHigher(membership);

  const {
    data: integrationsData,
    loading: integrationsLoading,
    error: integrationsError,
  } = useQuery<ListIntegrationsQuery, ListIntegrationsQueryVariables>(
    LIST_INTEGRATIONS,
    {
      variables: {
        input: {
          subaccountId: subaccount.id,
          services: [
            IntegrationServiceType.ActBlueWebhook,
            IntegrationServiceType.ActBlueImport,
          ],
        },
      },
      skip: !isAdmin,
    }
  );
  const hasDonationIntegration = !!integrationsData?.listIntegrations.length;

  const audiencesLabel = intl.formatMessage({
    defaultMessage: "Audiences",
    description: "Navigation label for the Audiences page",
  });

  const items: PrimarySidebarItem[] = [
    ...(isAdmin
      ? [
          {
            label: intl.formatMessage({
              defaultMessage: "Campaigns",
              description: "Navigation label for the Campaigns page",
            }),
            path: subaccountLink("/campaigns"),
            icon: <Campaign />,
            activeIcon: <CampaignFilled />,
          },
          {
            label: audiencesLabel,
            path: subaccountLink("/audiences"),
            icon: <SubscribersIcon />,
            activeIcon: <SubscribersIconFilled />,
          },
          {
            label: intl.formatMessage({
              defaultMessage: "People",
              description: "Navigation label for the People page",
            }),
            path: subaccountLink("/users"),
            icon: <Person />,
            activeIcon: <PersonFilled />,
          },
        ]
      : []),
    ...(userIsSubaccountSupervolunteerOrHigher(membership)
      ? [
          {
            label: intl.formatMessage({
              defaultMessage: "Threads",
              description: "Navigation label for the Threads page",
            }),
            path: subaccountLink("/threads"),
            icon: <Thread />,
            activeIcon: <ThreadFilled />,
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            label: intl.formatMessage({
              defaultMessage: "Inboxes",
              description: "Navigation label for the Inboxes page",
            }),
            path: subaccountLink("/inbox-management"),
            icon: <MailOutline />,
            activeIcon: <Mail />,
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Tags",
              description: "Navigation label for the Tags page",
            }),
            path: subaccountLink("/tags"),
            icon: <Tag />,
            activeIcon: <TagFilled />,
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Response Scripts",
              description: "Navigation label for the Response Scripts page",
            }),
            path: subaccountLink("/scripts"),
            icon: <Script />,
            activeIcon: <ScriptFilled />,
          },
          ...(personalizedGifsEnabled
            ? [
                {
                  label: intl.formatMessage({
                    defaultMessage: "Personalized GIFs",
                    description:
                      "Navigation label for the Personalized GIFs page",
                  }),
                  path: subaccountLink("/personalized-gifs"),
                  icon: <PersonalizedGifsIcon />,
                  activeIcon: <PersonalizedGifsIconFilled />,
                },
              ]
            : []),
          {
            label: intl.formatMessage({
              defaultMessage: "Opt Outs/Strike Lists",
              description: "Navigation label for the Opt Outs page",
            }),
            path: subaccountLink("/opt-outs"),
            icon: <OptOut />,
            activeIcon: <OptOutFilled />,
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Phone Numbers",
              description: "Navigation label for the Phone Numbers page",
            }),
            path: subaccountLink("/phone-numbers"),
            icon: <Phone sx={{ fontSize: "21px !important" }} />,
            activeIcon: <PhoneFilled sx={{ fontSize: "21px !important" }} />,
          },
          ...(donationHistoryPageEnabled && hasDonationIntegration
            ? [
                {
                  label: intl.formatMessage({
                    defaultMessage: "Donation History",
                    description:
                      "Navigation label for the Donation History page",
                  }),
                  path: subaccountLink("/donation-history"),
                  icon: <DonationHistory />,
                  activeIcon: <DonationHistoryFilled />,
                },
              ]
            : []),
          {
            label: intl.formatMessage({
              defaultMessage: "Usage",
              description: "Navigation label for the Usage page",
            }),
            path: subaccountLink("/usage"),
            icon: <Usage />,
            activeIcon: <UsageFilled />,
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Analytics",
              description: "Navigation label for the Analytics page",
            }),
            path: subaccountLink("/analytics"),
            icon: <PieChartOutline sx={{ fontSize: "21px !important" }} />,
            activeIcon: <PieChart sx={{ fontSize: "21px !important" }} />,
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Settings",
              description: "Navigation label for the Settings page",
            }),
            path: subaccountLink("/settings"),
            icon: <Settings sx={{ fontSize: "21px !important" }} />,
            activeIcon: <SettingsFilled sx={{ fontSize: "21px !important" }} />,
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Integrations",
              description: "Navigation label for the Integrations page",
            }),
            path: subaccountLink("/integrations"),
            icon: <Integration />,
            activeIcon: <IntegrationFilled />,
          },
        ]
      : []),
  ];
  const secondaryItems = [
    {
      label: intl.formatMessage({
        defaultMessage: "Switch to Texter",
        description: "Switch to the texter inbox view",
      }),
      path: subaccountLink("/inboxes"),
    },
  ];

  return (
    <AsyncWrapper loading={integrationsLoading} errors={[integrationsError]}>
      <Layout
        topBarIcon={
          !userIsSubaccountSupervolunteerOrHigher(membership) ? (
            <ArrowBackIcon />
          ) : undefined
        }
        topBarTitle={
          userIsSubaccountSupervolunteerOrHigher(membership)
            ? subaccount.name
            : `${subaccount.name} Campaigns`
        }
        topBarLink={subaccountLink(`/${subaccountLandingPage(membership)}`)}
        sidebarItems={items}
        sidebarSecondaryItems={secondaryItems}
      />
    </AsyncWrapper>
  );
}

export function SubaccountLayout(): ReactElement {
  const { subaccountSlug } = useParams();

  return (
    <SubaccountProvider explicitSubaccountSlug={subaccountSlug || null}>
      <CacheClearingApolloProvider>
        <SubaccountLayoutInner />
      </CacheClearingApolloProvider>
    </SubaccountProvider>
  );
}

export const subaccountRoutes: RouteObject[] = [
  {
    index: true,
    element: <SubaccountRootRedirect />,
  },
  {
    path: "campaigns",
    element: <CampaignsList />,
  },
  {
    path: "settings",
    element: <SubaccountSettings />,
  },
  {
    path: "threads",
    element: <ThreadsList />,
  },
  {
    path: "inbox-management",
    element: <InboxManagement />,
  },
  {
    path: "inboxes",
    element: <InboxList />,
  },
  {
    path: "inboxes/:inboxId/archived",
    element: <InboxArchived />,
  },
  {
    path: "inboxes/:inboxId",
    element: <Inbox />,
  },
  {
    path: "inboxes/:inboxId/campaigns/:campaignId",
    element: <InboxCampaign />,
  },
  {
    path: "inboxes/:inboxId/campaigns",
    element: <InboxAllCampaigns />,
  },
  {
    path: "scripts",
    element: <SubaccountResponseScripts />,
  },
  {
    path: "opt-outs",
    element: <OptOuts />,
  },
  {
    path: "campaigns/:campaignShortId",
    element: <CampaignView />,
  },
  {
    path: "campaigns/:campaignShortId/errors",
    element: <CampaignErrors />,
  },
  {
    path: "campaigns/:campaignShortId/edit/:section",
    element: <CampaignEditor />,
  },
  {
    path: "personalized-gifs",
    element: <PersonalizedGifs />,
  },
  {
    path: "phone-numbers",
    element: <PhoneNumbersList />,
  },
  {
    path: "phone-numbers/pools/:poolId",
    element: <PhoneNumberPoolDetails />,
  },
  {
    path: "phone-numbers/toll-free-registration",
    element: <TollFreeRegistration />,
  },
  {
    path: "10dlc/registration/:campaignRegistrationId",
    element: <Registration />,
  },
  {
    path: "10dlc/registration",
    element: <Registration />,
  },
  {
    path: "users",
    element: <SubaccountUsers />,
  },
  {
    path: "usage",
    element: <SubaccountUsage />,
  },
  {
    path: "integrations",
    element: <IntegrationList />,
  },
  {
    path: "tags",
    element: <Tags />,
  },
  {
    path: "shortcode",
    element: <ShortcodeRedirect />,
  },
  { path: "audiences/:audienceId/settings", element: <AudienceSettingsPage /> },
  { path: "audiences/:audienceId", element: <AudiencesPage /> },
  { path: "audiences", element: <AudiencesPage /> },
  {
    path: "analytics",
    element: <SubaccountAnalytics />,
  },
  {
    path: "donation-history",
    element: <SubaccountDonationHistory />,
  },
  {
    path: "approve/:campaignId",
    element: <Approver />,
  },
];
